import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, CreditCard, Download, ExternalLink, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';

const VendorCertificateModal = ({ user, onClose, onUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);

  if (!user) return null;

  console.log('VendorCertificateModal - User:', user);
  console.log('VendorCertificateModal - vendorCertificates:', user.vendorCertificates);

  const certificates = user.vendorCertificates || {};
  const hasBusinessLicense = certificates.businessLicense;
  const hasIdCard = certificates.idCard;
  const hasCertificates = hasBusinessLicense || hasIdCard;

  console.log('Has Business License:', hasBusinessLicense);
  console.log('Has ID Card:', hasIdCard);
  console.log('Has Certificates:', hasCertificates);

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = `http://localhost:5000${url}`;
    link.download = filename;
    link.click();
  };

  const handleView = (url) => {
    window.open(`http://localhost:5000${url}`, '_blank');
  };

  const handleFileUpload = async (event, certificateType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only images (JPG, PNG, GIF) and PDF files are allowed');
      return;
    }

    setIsUploading(true);
    setUploadingType(certificateType);

    try {
      const formData = new FormData();
      formData.append(certificateType, file);
      formData.append('userId', user._id);

      // Call API to upload certificate
      const response = await api.admin.uploadVendorCertificate(formData);

      toast.success(`${certificateType === 'businessLicenseFile' ? 'Business License' : 'ID Card'} uploaded successfully`);

      // Call onUpdate to refresh user data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Certificate upload error:', error);
      toast.error(error.message || 'Failed to upload certificate');
    } finally {
      setIsUploading(false);
      setUploadingType(null);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">Vendor Certificates</h2>
              <p className="text-orange-100 text-sm mt-1">
                {user.firstName} {user.lastName} ({user.email})
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
            {!hasCertificates ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No certificates uploaded</p>
                <p className="text-gray-400 text-sm mt-2 mb-6">
                  This vendor hasn't uploaded any certificates yet.
                </p>

                {/* Upload buttons for admin */}
                <div className="max-w-md mx-auto space-y-4">
                  <div>
                    <label htmlFor="admin-upload-business-license" className="cursor-pointer inline-block">
                      <div className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2">
                        <Upload className="w-5 h-5" />
                        {isUploading && uploadingType === 'businessLicenseFile' ? 'Uploading...' : 'Upload Business License'}
                      </div>
                    </label>
                    <input
                      id="admin-upload-business-license"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e, 'businessLicenseFile')}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </div>

                  <div>
                    <label htmlFor="admin-upload-id-card" className="cursor-pointer inline-block">
                      <div className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2">
                        <Upload className="w-5 h-5" />
                        {isUploading && uploadingType === 'idCardFile' ? 'Uploading...' : 'Upload ID Card'}
                      </div>
                    </label>
                    <input
                      id="admin-upload-id-card"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload(e, 'idCardFile')}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Upload Date */}
                {certificates.uploadedAt && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      <strong>Uploaded:</strong>{' '}
                      {new Date(certificates.uploadedAt).toLocaleDateString('en-SG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {/* Business License */}
                {hasBusinessLicense ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 text-orange-600 mr-2" />
                          <h3 className="font-semibold text-gray-900">Business License</h3>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(certificates.businessLicense)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink size={14} />
                            View
                          </button>
                          <button
                            onClick={() => handleDownload(certificates.businessLicense, 'business-license')}
                            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <Download size={14} />
                            Download
                          </button>
                          <label htmlFor="replace-business-license" className="cursor-pointer">
                            <div className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1">
                              <Upload size={14} />
                              Replace
                            </div>
                          </label>
                          <input
                            id="replace-business-license"
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload(e, 'businessLicenseFile')}
                            className="hidden"
                            disabled={isUploading}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="bg-gray-100 rounded-lg overflow-hidden">
                        {certificates.businessLicense.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img
                            src={`http://localhost:5000${certificates.businessLicense}`}
                            alt="Business License"
                            className="w-full h-auto max-h-96 object-contain"
                          />
                        ) : (
                          <div className="h-48 flex items-center justify-center bg-gray-200">
                            <div className="text-center">
                              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-gray-600 text-sm">PDF Document</p>
                              <p className="text-gray-500 text-xs mt-1">Click "View" to open</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-orange-300 rounded-lg p-6 bg-orange-50">
                    <div className="text-center">
                      <FileText className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                      <p className="text-orange-700 font-medium mb-2">Business License Missing</p>
                      <label htmlFor="upload-missing-business-license" className="cursor-pointer inline-block">
                        <div className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2">
                          <Upload className="w-4 h-4" />
                          {isUploading && uploadingType === 'businessLicenseFile' ? 'Uploading...' : 'Upload Business License'}
                        </div>
                      </label>
                      <input
                        id="upload-missing-business-license"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'businessLicenseFile')}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </div>
                  </div>
                )}

                {/* ID Card */}
                {hasIdCard ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CreditCard className="w-5 h-5 text-orange-600 mr-2" />
                          <h3 className="font-semibold text-gray-900">ID Card</h3>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(certificates.idCard)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink size={14} />
                            View
                          </button>
                          <button
                            onClick={() => handleDownload(certificates.idCard, 'id-card')}
                            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <Download size={14} />
                            Download
                          </button>
                          <label htmlFor="replace-id-card" className="cursor-pointer">
                            <div className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-1">
                              <Upload size={14} />
                              Replace
                            </div>
                          </label>
                          <input
                            id="replace-id-card"
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload(e, 'idCardFile')}
                            className="hidden"
                            disabled={isUploading}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="bg-gray-100 rounded-lg overflow-hidden">
                        {certificates.idCard.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img
                            src={`http://localhost:5000${certificates.idCard}`}
                            alt="ID Card"
                            className="w-full h-auto max-h-96 object-contain"
                          />
                        ) : (
                          <div className="h-48 flex items-center justify-center bg-gray-200">
                            <div className="text-center">
                              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-gray-600 text-sm">PDF Document</p>
                              <p className="text-gray-500 text-xs mt-1">Click "View" to open</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-orange-300 rounded-lg p-6 bg-orange-50">
                    <div className="text-center">
                      <CreditCard className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                      <p className="text-orange-700 font-medium mb-2">ID Card Missing</p>
                      <label htmlFor="upload-missing-id-card" className="cursor-pointer inline-block">
                        <div className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2">
                          <Upload className="w-4 h-4" />
                          {isUploading && uploadingType === 'idCardFile' ? 'Uploading...' : 'Upload ID Card'}
                        </div>
                      </label>
                      <input
                        id="upload-missing-id-card"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'idCardFile')}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VendorCertificateModal;
